/**
 * Ações de drop no rodapé: Excluir, Perdido, Venda, Fixado
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { PipelineCard } from '../types';
import { pipelineBoardKey } from './queryKeys';

export type DropZoneAction = 'excluir' | 'perdido' | 'venda' | 'fixado';

export function useCardDropActions(pipelineId: string) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const applyAction = useMutation({
    mutationFn: async ({
      card,
      action,
    }: {
      card: PipelineCard;
      action: DropZoneAction;
    }) => {
      if (!profile?.uuid) throw new Error('Usuário não autenticado');

      switch (action) {
        case 'excluir':
          await softDeleteCard(card.id, profile.uuid, 'Excluído');
          return null;
        case 'perdido':
          await moveToNamedStage(card, pipelineId, ['perdido', 'perda'], profile.uuid, 'Perdido');
          return null;
        case 'fixado':
          await moveToNamedStage(card, pipelineId, ['fixado'], profile.uuid, 'Fixado');
          return null;
        case 'venda': {
          const comercialPipelineId = await moveToComercialPipeline(card, profile.uuid);
          return comercialPipelineId;
        }
      }
    },
    onSuccess: (comercialPipelineId) => {
      queryClient.invalidateQueries({ queryKey: pipelineBoardKey(pipelineId) });
      if (comercialPipelineId) {
        queryClient.invalidateQueries({ queryKey: pipelineBoardKey(comercialPipelineId) });
      }
      // Cards transferidos aparecem na visão "Venda" via histórico
      queryClient.invalidateQueries({ queryKey: ['pipeline-transferred', pipelineId] });
      queryClient.invalidateQueries({ queryKey: ['client-journey'] });
    },
  });

  return { applyAction };
}

async function softDeleteCard(
  cardId: string,
  userId: string,
  motivo: string
): Promise<void> {
  const { error } = await supabase
    .from('pipeline_cards')
    .update({
      cancelado: true,
      motivo_cancelamento: motivo,
      data_ultima_atualizacao: new Date().toISOString(),
    })
    .eq('id', cardId);

  if (error) throw new Error(error.message);

  await supabase.from('card_history').insert({
    card_id: cardId,
    usuario_id: userId,
    acao: 'deletado',
    motivo,
  });
}

async function findStageByNames(pipelineId: string, names: string[]) {
  const { data: stages, error } = await supabase
    .from('pipeline_stages')
    .select('id, nome')
    .eq('pipeline_id', pipelineId)
    .order('ordem', { ascending: true });

  if (error) throw new Error(error.message);

  const normalized = names.map((n) => n.toLowerCase());
  return stages?.find((s) =>
    normalized.some((n) => s.nome.toLowerCase().includes(n))
  );
}

async function moveToNamedStage(
  card: PipelineCard,
  pipelineId: string,
  stageNames: string[],
  userId: string,
  motivo: string
): Promise<void> {
  const stage = await findStageByNames(pipelineId, stageNames);

  if (!stage) {
    throw new Error(`Configure uma etapa "${stageNames[0]}" nesta pipeline`);
  }

  const { error } = await supabase
    .from('pipeline_cards')
    .update({
      stage_id: stage.id,
      data_ultima_atualizacao: new Date().toISOString(),
    })
    .eq('id', card.id);

  if (error) throw new Error(error.message);

  await supabase.from('card_history').insert({
    card_id: card.id,
    usuario_id: userId,
    acao: 'movido_para_stage',
    stage_anterior_id: card.stage_id,
    stage_novo_id: stage.id,
    motivo,
  });
}

/**
 * Pipeline principal (fixa) de um tipo — a que é criada automaticamente para
 * cada tenant e não pode ser excluída/renomeada. É sempre única por
 * (tenant_id, tipo) — ver índice `pipelines_fixa_unica_por_tipo`.
 */
async function findPrincipalPipeline(tenantId: number, tipo: 'qualificacao' | 'venda' | 'pos_venda') {
  const { data, error } = await supabase
    .from('pipelines')
    .select('id, nome')
    .eq('tenant_id', tenantId)
    .eq('tipo', tipo)
    .eq('fixa', true)
    .eq('ativa', true)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Qualificação → Comercial: transfere o card para a primeira etapa da
 * pipeline principal de Comercial. O rastro fica no card_history
 * (acao=transferido + stage_anterior da Qualificação), que alimenta tanto a
 * timeline do lead quanto a visão "Venda" na pipeline de origem.
 */
async function moveToComercialPipeline(
  card: PipelineCard,
  userId: string
): Promise<string> {
  const comercialPipeline = await findPrincipalPipeline(card.tenant_id, 'venda');

  if (!comercialPipeline) {
    throw new Error('Pipeline Comercial principal não configurada');
  }

  const stage = await getFirstStage(comercialPipeline.id);

  if (!stage) {
    throw new Error('Pipeline Comercial sem etapas configuradas');
  }

  const { error } = await supabase
    .from('pipeline_cards')
    .update({
      pipeline_id: comercialPipeline.id,
      stage_id: stage.id,
      data_ultima_atualizacao: new Date().toISOString(),
    })
    .eq('id', card.id);

  if (error) throw new Error(error.message);

  await supabase.from('card_history').insert({
    card_id: card.id,
    usuario_id: userId,
    acao: 'transferido',
    stage_anterior_id: card.stage_id,
    stage_novo_id: stage.id,
    motivo: `Venda — Comercial / ${stage.nome}`,
  });

  return comercialPipeline.id;
}

async function getFirstStage(pipelineId: string) {
  const { data } = await supabase
    .from('pipeline_stages')
    .select('id, nome')
    .eq('pipeline_id', pipelineId)
    .order('ordem', { ascending: true })
    .limit(1)
    .maybeSingle();
  return data ?? undefined;
}
